// The assistant's entry point, in the left sidebar.
//
// It moved here from the top header, and it dresses like the nav items around
// it rather than like the pill it used to be: same padding, same hover, the
// same blue + bgSecondary treatment the selected route gets — with the panel
// being open standing in for "selected". The one difference from a nav item
// is deliberate: it is a button that opens an overlay, not a link that
// navigates, so it toggles and carries aria-expanded instead of aria-current.
//
// Just the button: the panel is mounted once by AiPanelHost in the layout.
// The same two conditions gate it as gate the mobile launcher:
//   1. The customer is signed in — without a token there is no account to
//      answer about.
//   2. The AI service accepts that token. It is a separate deployment from the
//      portal API, so it can be down or not yet rolled out while the rest of
//      the page works fine. AiPanelHost makes that check once.

import React from "react";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { AssistantIcon } from "icons/sidebar/AssistantIcon";
import { useAppDispatch } from "store/hooks";
import { useIsSmallSidebar } from "store/slices/commonSlice";
import { setAiOpen, useAiState } from "store/slices/aiSlice";
import { useUserData } from "store/slices/authSlice";

const AiSidebarButton = () => {
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const { available, open } = useAiState();
  const isSmallSidebar = useIsSmallSidebar();

  if (!userData?.token || !available) return null;

  const toggle = () => dispatch(setAiOpen(!open));

  const item = (
    <div
      className={`flex items-center gap-4 text-base font-medium leading-6 p-[7px] rounded-[5px] hover:!bg-bgSecondary ${
        open
          ? "bg-bgSecondary text-blue animate__animated animate__fadeIn"
          : "text-txtSecondary"
      }`}
    >
      <AssistantIcon
        aria-hidden="true"
        strokeColor={open ? "var(--color-blue)" : undefined}
      />
      {!isSmallSidebar ? (
        <div className="animate__animated animate__fadeIn">Assistant</div>
      ) : null}
    </div>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Open assistant"
      aria-expanded={open}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
      className="pr-[14px] pl-[17px] w-full cursor-pointer relative"
    >
      {open ? (
        <div className="h-10 w-[3px] bg-blue absolute left-0 rounded-tr-[6px] rounded-br-[6px] animate__animated animate__fadeIn"></div>
      ) : null}

      {isSmallSidebar ? (
        <CustomTooltip
          maxwidth="430px"
          place="right"
          id="Assistant"
          content={`<div class="text-sm leading-[18px] font-normal w-fit">Assistant</div>`}
        >
          {item}
        </CustomTooltip>
      ) : (
        item
      )}
    </div>
  );
};

export default AiSidebarButton;
